import { Brand, Country, Currency, Language, VOUCHER_EXTRA_TYPE } from '@/domainLogic/index.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { CONTENT_HUB_UPLOAD_URL } from '@/app/common/contentHubAssets.ts';
import { getDuration } from '../Analytics/utils';

export type VoucherExtra = {
  discountAmount: number;
  minSpend: number;
  voucherCode: string;
  type: VOUCHER_EXTRA_TYPE;
};

const VOUCHER_EXTRA_VALUES = {
  [Country.GB as string]: {
    [Currency.GBP as string]: {
      discountAmount: 100,
      minSpend: 1500,
      voucherCode: 'NEWYEARUK100',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
  [Country.DE as string]: {
    [Currency.EUR as string]: {
      discountAmount: 100,
      minSpend: 1500,
      voucherCode: 'NEUJAHR100',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
  [Country.FR as string]: {
    [Currency.EUR as string]: {
      discountAmount: 100,
      minSpend: 1500,
      voucherCode: 'NYEAR100',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
  [Country.IT as string]: {
    [Currency.EUR as string]: {
      discountAmount: 100,
      minSpend: 1500,
      voucherCode: 'NYEAR100',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
  [Country.ES as string]: {
    [Currency.EUR as string]: {
      discountAmount: 100,
      minSpend: 1500,
      voucherCode: 'NYEAR100',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
};

const VOUCHER_EXTRA_VALUES_BOOKING = {
  [Country.GB as string]: {
    [Currency.GBP as string]: {
      discountAmount: 80,
      minSpend: 1500,
      voucherCode: 'NYEARUK80',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
  [Country.DE as string]: {
    [Currency.EUR as string]: {
      discountAmount: 80,
      minSpend: 1500,
      voucherCode: 'NYEAR80',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
  [Country.FR as string]: {
    [Currency.EUR as string]: {
      discountAmount: 80,
      minSpend: 1500,
      voucherCode: 'NYEAR80',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
  [Country.IT as string]: {
    [Currency.EUR as string]: {
      discountAmount: 80,
      minSpend: 1500,
      voucherCode: 'NYEAR80',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
  [Country.ES as string]: {
    [Currency.EUR as string]: {
      discountAmount: 80,
      minSpend: 1500,
      voucherCode: 'NYEAR80',
      type: VOUCHER_EXTRA_TYPE.HUNDRED,
    },
  },
};

const VOUCHER_EXTRA_30_VALUES = {
  [Country.GB as string]: {
    [Currency.GBP as string]: {
      discountAmount: 30,
      minSpend: 600,
      voucherCode: 'XTRA30',
      type: VOUCHER_EXTRA_TYPE.THIRTY,
    },
  },
  [Country.DE as string]: {
    [Currency.EUR as string]: {
      discountAmount: 30,
      minSpend: 600,
      voucherCode: 'EXTRA30',
      type: VOUCHER_EXTRA_TYPE.THIRTY,
    },
  },
  [Country.FR as string]: {
    [Currency.EUR as string]: {
      discountAmount: 30,
      minSpend: 600,
      voucherCode: 'EXTRA30',
      type: VOUCHER_EXTRA_TYPE.THIRTY,
    },
  },
  [Country.IT as string]: {
    [Currency.EUR as string]: {
      discountAmount: 30,
      minSpend: 600,
      voucherCode: 'EXTRA30',
      type: VOUCHER_EXTRA_TYPE.THIRTY,
    },
  },
  [Country.ES as string]: {
    [Currency.EUR as string]: {
      discountAmount: 30,
      minSpend: 600,
      voucherCode: 'EXTRA30',
      type: VOUCHER_EXTRA_TYPE.THIRTY,
    },
  },
};

export const getVoucherExtraLink = (language: Language, brand: Brand, type: VOUCHER_EXTRA_TYPE) => {
  if (type === VOUCHER_EXTRA_TYPE.HUNDRED) {
    switch (language) {
      case Language.EN:
        if (brand === Brand.BOOKING) return `${CONTENT_HUB_UPLOAD_URL}/v1/DAM/Campaigns/2024/Jan-Sales/WL/TC/UK.pdf`;
        return 'https://www.lastminute.com/terms/promos/january-sale-voucher.html';
      case Language.FR:
        if (brand === Brand.BOOKING) return `${CONTENT_HUB_UPLOAD_URL}/v1/DAM/Campaigns/2024/Jan-Sales/WL/TC/FR.pdf`;
        return 'https://www.fr.lastminute.com/sejour/promotion-voyage/tc.html';
      case Language.DE:
        if (brand === Brand.BOOKING) return `${CONTENT_HUB_UPLOAD_URL}/v1/DAM/Campaigns/2024/Jan-Sales/WL/TC/DE.pdf`;
        return 'https://www.lastminute.de/urlaub/gutscheinbedingungen/';
      case Language.IT:
        if (brand === Brand.BOOKING) return `${CONTENT_HUB_UPLOAD_URL}/v1/DAM/Campaigns/2024/Jan-Sales/WL/TC/IT.pdf`;
        if (brand === Brand.VOLAGRATIS)
          return 'https://www.volagratis.com/it/offerte-speciali/saldi-di-gennaio/vouchers-termini-condizioni.html';
        return 'https://www.it.lastminute.com/offerte/saldi-di-gennaio/voucher-termini-e-condizioni/';
      case Language.ES:
        if (brand === Brand.BOOKING) return `${CONTENT_HUB_UPLOAD_URL}/v1/DAM/Campaigns/2024/Jan-Sales/WL/TC/ES.pdf`;
        if (brand === Brand.RUMBO) return 'https://www.rumbo.es/es/viajes/rebajas/terminos-y-condiciones.html';
        return 'https://www.es.lastminute.com/ofertas/rebajas/terminos-y-condiciones/';
      default:
        return null;
    }
  }
  if (type === VOUCHER_EXTRA_TYPE.THIRTY) {
    switch (language) {
      case Language.EN:
        return 'https://www.lastminute.com/promos/voucher-terms-conditions.html';
      case Language.FR:
        return 'https://www.fr.lastminute.com/promo/voucher-terms-conditions-2.html';
      case Language.DE:
        return 'https://www.lastminute.de/de/promo/voucher-terms-conditions.html';
      case Language.IT:
        if (brand === Brand.VOLAGRATIS) return 'https://www.volagratis.com/promo/voucher-terms-conditions.html';
        return 'https://www.it.lastminute.com/promo/voucher-terms-conditions.html';
      case Language.ES:
        if (brand === Brand.RUMBO) return 'https://www.rumbo.es/es/ofertas-especiales/voucher-terms-conditions.html';
        return 'https://www.es.lastminute.com/ofertas/promo/voucher-terms-conditions.html';
      default:
        return null;
    }
  }
  return null;
};

export const getVoucherExtraValuesByType = (
  type: VOUCHER_EXTRA_TYPE,
  brand: Brand,
  country: Country,
  currency: Currency
): VoucherExtra | null => {
  switch (type) {
    case VOUCHER_EXTRA_TYPE.HUNDRED:
      if (brand === Brand.BOOKING) return VOUCHER_EXTRA_VALUES_BOOKING?.[country]?.[currency];
      return VOUCHER_EXTRA_VALUES?.[country]?.[currency];
    case VOUCHER_EXTRA_TYPE.THIRTY:
      return VOUCHER_EXTRA_30_VALUES?.[country]?.[currency];
    default:
      return null;
  }
};

export const voucherExtraBannerSupported = (
  type: VOUCHER_EXTRA_TYPE,
  brand: Brand,
  country: Country,
  currency: Currency
): boolean => !!getVoucherExtraValuesByType(type, brand, country, currency);

export const getCheckVoucherExtraValues = (
  searchParams: SearchParams,
  brand: Brand,
  country: Country,
  currency: Currency
): VoucherExtra | null => {
  const dateFrom = searchParams?.[URLParameters.DATE_FROM];
  const dateTo = searchParams?.[URLParameters.DATE_TO];
  const type = VOUCHER_EXTRA_TYPE.THIRTY; // TODO: only 30 is currently supported

  const numOfNights = dateFrom && dateTo ? getDuration({ dateFrom, dateTo }) : 0;
  if (numOfNights === 0 || !getVoucherExtraValuesByType(type, brand, country, currency)) return null;

  return getVoucherExtraValuesByType(type, brand, country, currency);
};
